/**
 * LocationsGoogleMap Component
 * Interactive Google Map showing all location markers
 *
 * RBAC Integration:
 * - System admins (super_admin + support): See precise coordinates
 * - Regular users: See city-level (fuzzy) coordinates
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, useJsApiLoader, InfoWindow, Marker } from '@react-google-maps/api';
import type { Location, Country } from '@simcheck/shared-types';
import { MapPin, Smartphone, Building2, FlaskConical, Home, Truck } from 'lucide-react';
import { Badge, Card, Spinner } from '@/components/ui';
import { useTheme } from '@/providers/ThemeProvider';
import { geocodeCity } from '@/services/geocodingService';
import { getMapOptions } from '@/config/mapStyles';
import { AdvancedMarker } from '@/components/maps/AdvancedMarker';

// 'marker' library is required for AdvancedMarkerElement.
const GOOGLE_MAPS_LIBRARIES: ('places' | 'marker')[] = ['places', 'marker'];

interface LocationsGoogleMapProps {
  locations: Location[];
  countries: Country[];
  canViewSensitiveData: boolean;
  onLocationSelect?: (locationId: string) => void;
  selectedLocationId?: string;
  height?: string;
  /**
   * Reports how many of the passed locations actually got a marker. A location
   * with no coordinates that also fails to geocode is dropped silently — the
   * caller needs this to avoid captioning the map with a count it isn't showing.
   */
  onMappedCountChange?: (mappedCount: number) => void;
}

interface MapLocation {
  location: Location;
  coordinates: { lat: number; lng: number };
  isExact: boolean;
  countryName?: string;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = { lat: 30.0, lng: 0.0 };

// Colorful location type markers for clear visual differentiation
const LOCATION_TYPE_COLORS: Record<Location['type'], string> = {
  datacenter: '#3B82F6', // blue
  office: '#10B981',     // green
  lab: '#8B5CF6',        // purple
  home: '#F59E0B',       // amber
  mobile: '#EF4444',     // red
};

const LOCATION_TYPE_ICONS: Record<Location['type'], React.ComponentType<{ className?: string }>> = {
  datacenter: Building2,
  office: Building2,
  lab: FlaskConical,
  home: Home,
  mobile: Truck,
};

const STATUS_COLORS: Record<Location['status'], { bg: string; text: string }> = {
  active: { bg: 'bg-status-success-bg', text: 'text-status-success' },
  maintenance: { bg: 'bg-status-warning-bg', text: 'text-status-warning' },
  offline: { bg: 'bg-status-offline-bg', text: 'text-status-offline' },
  decommissioned: { bg: 'bg-muted', text: 'text-muted-foreground' },
};

export const LocationsGoogleMap: React.FC<LocationsGoogleMapProps> = ({
  locations,
  countries,
  canViewSensitiveData,
  onLocationSelect,
  selectedLocationId,
  height = '400px',
  onMappedCountChange,
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState<MapLocation | null>(null);
  const [mapLocations, setMapLocations] = useState<MapLocation[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || '';

  // Use hook instead of LoadScript to prevent "google api is already presented" errors
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Process locations and determine coordinates. Gated on `isLoaded` because
  // the city-name fallback geocodes through the Maps JS API — running before it
  // is ready would drop every coordinate-less location from the map.
  useEffect(() => {
    const processLocations = async () => {
      if (locations.length === 0) {
        setMapLocations([]);
        return;
      }
      if (!isLoaded) return;

      setIsProcessing(true);
      const processed: MapLocation[] = [];

      for (const location of locations) {
        let coords: { lat: number; lng: number } | null = null;
        let isExact = false;
        const country = countries.find((c) => c.id === location.countryId);

        // For system admins with exact coordinates
        if (canViewSensitiveData && location.coordinates) {
          coords = {
            lat: location.coordinates.latitude,
            lng: location.coordinates.longitude,
          };
          isExact = true;
        }
        // For regular users: use stored coordinates at city-level precision, or geocode
        else if (location.coordinates) {
          // Use stored coordinates but rounded to ~city level (~0.01 degree ≈ 1km)
          coords = {
            lat: Math.round(location.coordinates.latitude * 100) / 100,
            lng: Math.round(location.coordinates.longitude * 100) / 100,
          };
          isExact = false;
        }
        else if (location.city) {
          const geocoded = await geocodeCity(location.city, country?.name);
          if (geocoded) {
            coords = { lat: geocoded.latitude, lng: geocoded.longitude };
            isExact = false;
          }
        }

        if (coords) {
          processed.push({
            location,
            coordinates: coords,
            isExact,
            countryName: country?.name,
          });
        } else {
          console.warn(
            `[LocationsGoogleMap] "${location.displayName || location.name}" has no coordinates and could not be geocoded — not shown on the map.`,
          );
        }
      }

      setMapLocations(processed);
      setIsProcessing(false);
    };

    processLocations();
  }, [locations, countries, canViewSensitiveData, isLoaded]);

  useEffect(() => {
    onMappedCountChange?.(mapLocations.length);
  }, [mapLocations.length, onMappedCountChange]);

  // Fit bounds when locations change
  useEffect(() => {
    if (!mapRef.current || mapLocations.length === 0 || !mapLoaded) return;

    if (mapLocations.length === 1) {
      mapRef.current.setCenter(mapLocations[0].coordinates);
      mapRef.current.setZoom(10);
    } else {
      const bounds = new google.maps.LatLngBounds();
      mapLocations.forEach((ml) => bounds.extend(ml.coordinates));
      mapRef.current.fitBounds(bounds, 50);
    }
  }, [mapLocations, mapLoaded]);

  const handleMarkerClick = useCallback((mapLocation: MapLocation) => {
    setSelectedMarker(mapLocation);
  }, []);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapLoaded(true);
  }, []);

  const getMarkerIcon = useCallback(
    (locationType: Location['type'], isSelected: boolean): { url: string; size: { width: number; height: number } } => {
      const color = LOCATION_TYPE_COLORS[locationType] || '#6B7280';
      const size = isSelected ? 44 : 32;

      // SVG marker with location type color
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
          <path fill="${color}" stroke="white" stroke-width="1" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
          <circle cx="12" cy="9" r="3" fill="white"/>
        </svg>
      `;

      return {
        url: `data:image/svg+xml;base64,${btoa(svg)}`,
        size: { width: size, height: size },
      };
    },
    []
  );

  // No API key configured
  if (!apiKey) {
    return (
      <Card className="p-6 flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg font-semibold text-foreground mb-2">Map Not Available</p>
          <p className="text-sm text-muted-foreground">
            Google Maps API key is not configured.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Add VITE_GOOGLE_MAPS_API_KEY to your environment.
          </p>
        </div>
      </Card>
    );
  }

  // No locations to display
  if (locations.length === 0) {
    return (
      <Card className="p-6 flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg font-semibold text-foreground mb-2">No Locations</p>
          <p className="text-sm text-muted-foreground">
            Create locations to see them on the map.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="relative" style={{ height }}>
      {/* Legend */}
      <div className="absolute top-4 left-4 z-10 bg-card/95 backdrop-blur-sm rounded-lg p-3 border border-border shadow-lg">
        <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
          Location Types
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {Object.entries(LOCATION_TYPE_COLORS).map(([type, color]) => (
            <div key={type} className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded-full border border-white/50"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs capitalize text-foreground">{type}</span>
            </div>
          ))}
        </div>
        {!canViewSensitiveData && (
          <div className="mt-2 pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground/70 italic">
              Showing city-level locations
            </p>
          </div>
        )}
      </div>

      {/* Processing indicator */}
      {isProcessing && (
        <div className="absolute top-4 right-4 z-10 bg-card/95 backdrop-blur-sm rounded-lg px-3 py-2 border border-border flex items-center gap-2">
          <Spinner size="sm" />
          <p className="text-xs text-muted-foreground">Loading locations...</p>
        </div>
      )}

      {/* Error state */}
      {loadError && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20">
          <div className="text-center">
            <p className="text-status-error font-medium">Error loading map</p>
            <p className="text-sm text-muted-foreground">{loadError.message || 'Failed to load Google Maps'}</p>
          </div>
        </div>
      )}

      {/* Loading state */}
      {!isLoaded && !loadError && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-20">
          <div className="flex items-center gap-2">
            <Spinner size="md" />
            <p className="text-muted-foreground">Loading map...</p>
          </div>
        </div>
      )}

      {isLoaded && (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapLocations[0]?.coordinates || defaultCenter}
          zoom={3}
          options={getMapOptions(mapId || undefined, isDarkMode)}
          onLoad={handleMapLoad}
        >
          {mapLoaded &&
            mapLocations.map((ml) => {
              const icon = getMarkerIcon(ml.location.type, ml.location.id === selectedLocationId);
              const title = ml.location.displayName || ml.location.name;

              // AdvancedMarkerElement requires a Map ID; fall back to the legacy
              // Marker (with inline map styles) when mapId isn't configured.
              return mapId ? (
                <AdvancedMarker
                  key={ml.location.id}
                  position={ml.coordinates}
                  onClick={() => handleMarkerClick(ml)}
                  iconUrl={icon.url}
                  iconSize={icon.size}
                  title={title}
                />
              ) : (
                <Marker
                  key={ml.location.id}
                  position={ml.coordinates}
                  onClick={() => handleMarkerClick(ml)}
                  icon={{
                    url: icon.url,
                    scaledSize: new google.maps.Size(icon.size.width, icon.size.height),
                    anchor: new google.maps.Point(icon.size.width / 2, icon.size.height),
                  }}
                  title={title}
                />
              );
            })}

          {selectedMarker && (
            <InfoWindow
              position={selectedMarker.coordinates}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div className="min-w-[220px] p-1">
                <h3 className="font-semibold text-gray-900 mb-1 text-sm">
                  {selectedMarker.location.displayName || selectedMarker.location.name}
                </h3>
                <p className="text-gray-600 text-xs mb-2">
                  {selectedMarker.location.city}
                  {selectedMarker.countryName && `, ${selectedMarker.countryName}`}
                </p>

                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${STATUS_COLORS[selectedMarker.location.status].bg} ${STATUS_COLORS[selectedMarker.location.status].text}`}
                  >
                    {selectedMarker.location.status}
                  </span>
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize border"
                    style={{
                      borderColor: LOCATION_TYPE_COLORS[selectedMarker.location.type],
                      color: LOCATION_TYPE_COLORS[selectedMarker.location.type],
                    }}
                  >
                    {selectedMarker.location.type}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-gray-500 text-xs">
                  <span className="flex items-center gap-1">
                    <Smartphone className="w-3 h-3" />
                    {selectedMarker.location.availableModemCount || 0}/
                    {selectedMarker.location.modemCount || 0} modems
                  </span>
                </div>

                {canViewSensitiveData && selectedMarker.isExact && (
                  <p className="text-xs text-gray-400 mt-2 font-mono">
                    {selectedMarker.coordinates.lat.toFixed(6)},{' '}
                    {selectedMarker.coordinates.lng.toFixed(6)}
                  </p>
                )}

                {!selectedMarker.isExact && (
                  <p className="text-xs text-amber-600 mt-2 italic">
                    Approximate location (city center)
                  </p>
                )}

                {onLocationSelect && (
                  <button
                    onClick={() => onLocationSelect(selectedMarker.location.id)}
                    className="mt-3 w-full px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    View Details
                  </button>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </div>
  );
};
